import { useEffect } from "react";

import { OutcomeScoreTable } from "./OutcomeScoreTable";
import { PagePointTable } from "./PagePointTable";
import { SectionScoreTable } from "./SectionScoreTable";
import { useFetchAssessmentBaseData, useFetchAssessmentData } from "./assessment-data";
import { useSectionScoreData } from "./hooks";

import { serverGetSectionScoreTable } from "api/api-server-assessment";
import { EmptyAssessmentDashboard } from "components/materials/section/assessment/EmptyAssessmentDashboard";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSection, selectCurrentSectionId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

export const SectionScoreDashboard = () => {
  const currentSection = useAppSelector(selectCurrentSection);
  const sectionId = useAppSelector(selectCurrentSectionId);

  const {
    studentPointsPerPageCSVData,
    studentPointsPerPageTableRows,
    studentScoresPerOutcomeCSVData,
    studentScoresPerOutcomeTableRows,
    sectionStudents,
    sectionOutcomes,
    publishedPagesWithAssessments,
  } = useSectionScoreData();

  useFetchAssessmentBaseData();
  useFetchAssessmentData();

  useEffect(() => {
    if (sectionId) {
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.page_group_users.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const { data: sectionScoreTableRows } = usePromiseEffect(async () => {
    return sectionId ? serverGetSectionScoreTable(sectionId) : null;
  }, [sectionId]);

  const activitiesMaxValue = currentSection?.section_score_activities_percentage ?? 0;
  const prepWorkMaxValue = currentSection?.section_score_prepwork_percentage ?? 0;
  const assignmentsMaxValue = currentSection?.section_score_assignments_percentage ?? 0;

  const noData =
    (!sectionScoreTableRows || sectionScoreTableRows.length === 0) &&
    publishedPagesWithAssessments.length === 0 &&
    studentScoresPerOutcomeTableRows.every(
      (row) => row.outcomeScores.length === 0 || row.outcomeScores.every((score) => score === null),
    );

  if (!sectionId) {
    return null;
  }

  if (noData) {
    return <EmptyAssessmentDashboard />;
  }

  return (
    <main className="assessment-dashboard">
      <SectionScoreTable
        activitiesMaxValue={activitiesMaxValue}
        assignmentsMaxValue={assignmentsMaxValue}
        prepWorkMaxValue={prepWorkMaxValue}
        sectionId={sectionId}
        sectionScoreTableRows={sectionScoreTableRows}
      />

      <PagePointTable
        publishedPagesWithAssessments={publishedPagesWithAssessments}
        sectionStudents={sectionStudents}
        studentPointsPerPageCSVData={studentPointsPerPageCSVData}
        studentPointsPerPageTableRows={studentPointsPerPageTableRows}
      />

      <OutcomeScoreTable
        sectionOutcomes={sectionOutcomes}
        studentScoresPerOutcomeCSVData={studentScoresPerOutcomeCSVData}
        studentScoresPerOutcomeTableRows={studentScoresPerOutcomeTableRows}
      />
    </main>
  );
};
